import { getCurrencySymbol } from "@angular/common";
import { BudgetView } from "./budget.view";
import {
  BudgetAmounts,
  BudgetIntervalType,
  TransactionAmountsTime,
} from "@bitwarden/web-vault/app/models/types/budget.types";
import { BudgetResultView } from "../../../services/store/calculated-result.collection.abstraction";
import { WebWorkerQueue } from "../../../services/web-worker/WebWorkerQueue";
import { BalanceDatesUtils } from "@bitwarden/web-vault/app/services/store/calculation-base/balances/balanceDates.utils";
import { addWeeks, addMonths, format, getISOWeek } from "date-fns";
import { WorkerMessage } from "@bitwarden/web-vault/web-worker/worker.message";
import { TransactionBalancesWorkerMessage } from "@bitwarden/web-vault/app/services/web-worker/transaction-balances/transaction-balances.worker.message";
import { TransactionBalancesWorkerResult } from "@bitwarden/web-vault/app/services/web-worker/transaction-balances/transaction-balances.worker.result";

export type BudgetIntervalSpend = {
  budgeted: number;
  actual: number; // signed sum of transaction amounts in the current interval (spend is negative)
  spent: number; // absolute amount spent
  remaining: number; // budgeted + actual (negative when over budget)
  usedPct: number; // spent / budgeted * 100 (uncapped)
  status: "under" | "approaching" | "over";
  categoryActualAmounts: Map<string, number>; // signed sum per category for the current interval
};

export type BudgetPageInterval = Extract<BudgetIntervalType, "monthly" | "weekly">;

export class BudgetUtils {
  /**
   * True when another budget already uses this name (case-insensitive, trimmed). Pass the
   * budget's own id as `excludeId` when editing so it doesn't collide with itself.
   */
  static isNameDuplicate(
    name: string | null | undefined,
    budgets: BudgetView[],
    excludeId?: string | null,
  ): boolean {
    const target = (name ?? "").trim().toLowerCase();
    return budgets.some((b) => b.id !== excludeId && b.name.trim().toLowerCase() === target);
  }

  /**
   * Budgets whose name contains the search text (case-insensitive, trimmed); a blank search keeps
   * every budget. The one search predicate shared by the budget list and the Period filter counts.
   */
  static filterBySearch(budgets: BudgetView[], searchText: string): BudgetView[] {
    const search = searchText.trim().toLowerCase();
    return search
      ? budgets.filter((budget) => budget.name?.toLowerCase().includes(search))
      : budgets;
  }

  /**
   * Budget counts per cadence for the Period filter menu, over the budgets the search box leaves
   * in the list — so a count always matches the number of rows that cadence would show.
   */
  static countByPeriod(
    budgets: BudgetView[],
    searchText: string,
  ): { all: number; monthly: number; weekly: number } {
    const matching = this.filterBySearch(budgets, searchText);
    return {
      all: matching.length,
      monthly: matching.filter((budget) => budget.interval === "monthly").length,
      weekly: matching.filter((budget) => budget.interval === "weekly").length,
    };
  }

  /**
   * Classify a used percentage into a status band for progress-bar colours and the header legend.
   * Thresholds are tunable: under < 75%, approaching 75–99.99%, over >= 100%. A fully-spent
   * budget (exactly 100%) counts as over so the list view and the budget detail panel agree.
   */
  static budgetStatus(usedPct: number): "under" | "approaching" | "over" {
    if (usedPct >= 100) {
      return "over";
    }
    if (usedPct >= 75) {
      return "approaching";
    }
    return "under";
  }

  /** Fill colour for a progress bar / legend dot based on the spend status band */
  static statusBarClass(status: "under" | "approaching" | "over"): string {
    switch (status) {
      case "over":
        return "tw-bg-color-status-error-text";
      case "approaching":
        return "tw-bg-color-status-warning";
      default:
        return "tw-bg-color-status-success-text";
    }
  }

  /** Text colour for a variance figure: positive (under budget), negative (overspent), zero. */
  static varianceTextClass(variance: number): string {
    if (variance > 0) {
      return "tw-text-color-money-in-text";
    }
    if (variance < 0) {
      return "tw-text-color-status-error-text";
    }
    return "tw-text-color-text-tertiary";
  }

  /** Classes for the "Overspent" tag shown next to a negative variance; empty when not overspent. */
  static varianceTagClass(variance: number): string {
    return variance < 0
      ? "tw-bg-color-status-error-bg tw-text-color-status-error-text tw-px-2 tw-py-[2px] tw-rounded-lg tw-text-[11px] tw-font-medium"
      : "";
  }

  /**
   * Currency-symbol-prefixed amount (e.g. "$123.45"), shared by the history ag-grid table and its
   * compact-list fallback below the `xl` breakpoint so the same figures render identically on
   * both sides of that layout switch instead of drifting into two different formats.
   */
  static formatCurrency(value: number | null, currencyCode?: string): string {
    if (value == null) {
      return "-";
    }
    const symbol = currencyCode ? getCurrencySymbol(currencyCode, "narrow") : "";
    const formatted = value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${symbol}${formatted}`;
  }

  /**
   * Start (unix ms) of the budget's CURRENT interval, mirroring getStartingDate() semantics
   * (startingPreference is day-of-month for monthly, day-of-week for weekly, month index for yearly).
   */
  static getCurrentIntervalStart(budget: BudgetView, nowMs: number = Date.now()): number {
    return this.getIntervalStart(budget.interval, budget.startingPreference ?? 1, nowMs);
  }

  static getIntervalStartForBudget(
    budget: BudgetView,
    interval: BudgetIntervalType,
    nowMs: number = Date.now(),
  ): number {
    return this.getIntervalStart(interval, this.getStartingPreference(budget, interval), nowMs);
  }

  static getIntervalStart(
    interval: BudgetIntervalType,
    startingPreference = 1,
    nowMs: number = Date.now(),
  ): number {
    const now = new Date(nowMs);

    switch (interval) {
      case "weekly": {
        const todayDay = now.getDay();
        const daysBack = (todayDay + 7 - startingPreference) % 7;
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - daysBack);
        return BalanceDatesUtils.getStartOfDayDate(weekStart).getTime();
      }
      default: {
        const monthStart = new Date(now.getFullYear(), now.getMonth(), startingPreference);
        if (monthStart.getTime() > now.getTime()) {
          monthStart.setMonth(monthStart.getMonth() - 1);
        }
        return BalanceDatesUtils.getStartOfDayDate(monthStart).getTime();
      }
    }
  }

  /**
   * Synchronously compute the current-interval spend for a single budget by summing the amounts of
   * its linked categories' transactions within the current interval. No web worker — cheap enough to
   * run for every budget on the list page. Ignores budget `spread`, so it may differ slightly from the
   * worker-based detail calculation for non-continuous spread budgets (acceptable for a summary).
   */
  static calculateCurrentIntervalSpend(
    budget: BudgetView,
    transactionsByCat: Map<string, TransactionAmountsTime[]>,
    nowMs: number = Date.now(),
    budgetedAmount?: number,
    intervalOverride?: BudgetIntervalType,
    // Optional per-transaction converter (core → budget currency at the transaction's date). When
    // supplied, pass a native budgetedAmount so spend and budget are in the same (budget) currency.
    convert?: (coreAmount: number, date: Date) => number,
  ): BudgetIntervalSpend {
    const budgeted = budgetedAmount ?? budget.amount ?? 0;
    const intervalStart = intervalOverride
      ? this.getIntervalStart(
          intervalOverride,
          this.getStartingPreference(budget, intervalOverride),
          nowMs,
        )
      : this.getCurrentIntervalStart(budget, nowMs);
    const categoryIds = this.getCategoryIdsInBudget(budget);

    let actual = 0;
    const categoryActualAmounts = new Map<string, number>();
    for (const categoryId of categoryIds) {
      let categoryAmount = 0;
      const transactions = transactionsByCat?.get(categoryId) ?? [];
      for (const transaction of transactions) {
        if (transaction.timeStamp >= intervalStart && transaction.timeStamp <= nowMs) {
          categoryAmount += convert
            ? convert(transaction.amount, new Date(transaction.timeStamp))
            : transaction.amount;
        }
      }
      categoryActualAmounts.set(categoryId, categoryAmount);
      actual += categoryAmount;
    }

    const spent = Math.max(-actual, 0); // only outflows count as spend; net-positive intervals (refunds) are not spend
    const remaining = budgeted + actual; // inflows restore headroom (matches the doc comment)
    const usedPct = budgeted ? (spent / budgeted) * 100 : 0;

    return {
      budgeted,
      actual,
      spent,
      remaining,
      usedPct,
      status: this.budgetStatus(usedPct),
      categoryActualAmounts,
    };
  }

  /**
   * Start of the next interval — always CALENDAR-aligned (1st of next month, next Monday,
   * Jan 1 next year), independent of budget.startingPreference or creation day. A monthly
   * budget created on the 9th still resets on the 1st, not the 9th.
   */
  static getIntervalResetDate(interval: BudgetIntervalType, nowMs: number = Date.now()): Date {
    const now = new Date(nowMs);
    switch (interval) {
      case "weekly": {
        // Monday-anchored: getIntervalStart(..1..) is the current week's Monday; +7 = next Monday.
        const resetDate = new Date(this.getIntervalStart("weekly", 1, nowMs));
        resetDate.setDate(resetDate.getDate() + 7);
        return resetDate;
      }
      default:
        return new Date(now.getFullYear(), now.getMonth() + 1, 1);
    }
  }

  static getDaysLeftInInterval(interval: BudgetIntervalType, nowMs: number = Date.now()): number {
    const resetMs = this.getIntervalResetDate(interval, nowMs).getTime();
    // floor, not ceil: count only whole days ahead, excluding the in-progress current day —
    // Monday 1:16am on a weekly budget has 6 days left (Tue–Sun), not 7.
    return Math.max(0, Math.floor((resetMs - nowMs) / 86_400_000));
  }

  /** Header label for the monthly view. The weekly view uses getWeekLabelParts() instead. */
  static getPageIntervalLabel(nowMs: number = Date.now()): string {
    return format(new Date(nowMs), "MMMM yyyy");
  }

  /** getPageIntervalLabel split into month and year, for the header's compact (stacked) title. */
  static getPageIntervalLabelParts(nowMs: number = Date.now()): { month: string; year: string } {
    const date = new Date(nowMs);
    return { month: format(date, "MMMM"), year: format(date, "yyyy") };
  }

  /**
   * Structured label for the current week (Monday-anchored). ISO week number is used so the number
   * is stable when a week straddles a month/year boundary; the date range makes the span explicit.
   */
  static getWeekLabelParts(nowMs: number = Date.now()): {
    week: string;
    range: string;
    year: string;
  } {
    const start = new Date(this.getIntervalStart("weekly", 1, nowMs));
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return {
      week: `Week ${getISOWeek(start)}`,
      range: `${format(start, "d MMM")} - ${format(end, "d MMM")}`,
      year: format(end, "yyyy"),
    };
  }

  static getProgressLabel(interval: BudgetIntervalType): string {
    return interval === "weekly" ? "Progress this week" : "Progress this month";
  }

  private static getStartingPreference(budget: BudgetView, interval: BudgetIntervalType): number {
    if (budget.interval === interval) {
      return budget.startingPreference ?? 1;
    }
    return 1;
  }

  /**
   * Returns the starting date of the budget view using the start date property, the
   * frequency, startingPreference and the number of periods we need to go back to find the starting date
   */
  static getStartingDate(budget: BudgetView, periods = 3, useSetStartDate = false): Date {
    // if useSetStartDate is true and the budget has a start date set, use that
    if (useSetStartDate && budget.startDate) {
      return budget.startDate;
    } else {
      const startingPreference = budget.startingPreference ?? 1;
      const today = new Date();
      let startDate: Date;
      if (budget.interval === "weekly") {
        startDate = new Date(today);
        const todayDay = today.getDay();
        // modulo already lands on the most recent reset day, so the last generated interval
        // covers the current week — matching monthly and getCurrentIntervalStart()
        const daysToSubtract = ((todayDay + 7 - startingPreference) % 7) + periods * 7;
        startDate.setDate(today.getDate() - daysToSubtract);
      } else {
        startDate = new Date(today);
        const currentDay = today.getDate();
        if (startingPreference > currentDay) {
          startDate.setMonth(today.getMonth() - (periods + 1));
        } else {
          startDate.setMonth(today.getMonth() - periods);
        }
        startDate.setDate(startingPreference);
      }

      return startDate;
    }
  }

  /**
   * Get the unique category IDs linked to the budget
   * @param budgetView
   */
  static getCategoryIdsInBudget(budgetView: BudgetView): string[] {
    const categoryIds: Set<string> = new Set<string>();
    for (const category of budgetView.categoriesLink) {
      categoryIds.add(category.id);
    }
    return Array.from(categoryIds);
  }

  /**
   * Calculate the budget results for the given budget view and the number of periods to go back
   * @param budgetView
   * @param webWorkerQueue
   * @param transactionsByCat
   * @param period
   */
  static async calculateBudgetResults(
    budgetView: BudgetView,
    webWorkerQueue: WebWorkerQueue,
    transactionsByCat: Map<string, TransactionAmountsTime[]>,
    period = 3,
  ): Promise<BudgetResultView> {
    const budgetResult = new BudgetResultView();
    const resultsByIntervals = new Map<number, BudgetAmounts>();
    const budgetCategories = this.getCategoryIdsInBudget(budgetView);
    const startDate = BalanceDatesUtils.getStartOfDayDate(
      this.getStartingDate(budgetView, period, false),
    );
    const startDateTime = startDate.getTime();

    const budgetIntervalTimes = this.getBudgetIntervalTimes(
      startDate,
      budgetView.interval,
      period,
      true,
    );

    this.setupBudgetResults(
      budgetResult,
      budgetView,
      budgetIntervalTimes,
      resultsByIntervals,
      startDate,
    );

    const promises: Promise<WorkerMessage>[] = [];

    // get the lists of transactionAmounts that are relevant to the categories in the budget
    for (const categoryId of budgetCategories) {
      const transactionsForCategory = transactionsByCat.get(categoryId);
      if (transactionsForCategory && transactionsForCategory.length > 0) {
        const nowMs = Date.now();
        const transactionsWithinDateRange = transactionsForCategory.filter((transaction) => {
          return transaction.timeStamp >= startDateTime && transaction.timeStamp <= nowMs;
        });

        // if there are transactions in the date range then process the dates for the category
        if (transactionsWithinDateRange.length > 0) {
          const transactionDates = transactionsWithinDateRange.map((t) =>
            Math.floor(t.timeStamp / 1000),
          );
          const transactionsAmounts = transactionsWithinDateRange.map((t) => t.amount);

          const transactionDatesArray = new Uint32Array(transactionDates);
          const transactionAmountsArray = new Float64Array(transactionsAmounts);

          const balanceRequest = new TransactionBalancesWorkerMessage(
            "getBalanceForBudget",
            "getBudgetBalances",
            transactionDatesArray.buffer,
            transactionAmountsArray.buffer,
            0,
            new Uint32Array(budgetIntervalTimes).buffer,
          );

          // Get the balances for the category and update the budget amounts accordingly
          promises.push(
            webWorkerQueue.postMessagePromise(balanceRequest).then(
              (workerMessage: TransactionBalancesWorkerResult) => {
                if (workerMessage?.balanceAmounts) {
                  const balanceAmounts = new Float64Array(workerMessage.balanceAmounts);
                  const balanceDates = new Uint32Array(workerMessage.balanceDates);

                  for (let i = 0; i < balanceAmounts.length; i++) {
                    const balanceDate = balanceDates[i];
                    const balanceAmount = balanceAmounts[i];

                    const budgetAmounts = resultsByIntervals.get(balanceDate);
                    if (budgetAmounts) {
                      // update the actual amount
                      budgetAmounts.actualAmount += balanceAmount;
                      // update the variance amount
                      budgetAmounts.varianceAmount =
                        budgetAmounts.budgetAmount + budgetAmounts.actualAmount;

                      // update the category actual amounts
                      const currentCategoryAmount =
                        budgetAmounts.categoryActualAmounts.get(categoryId) || 0;
                      budgetAmounts.categoryActualAmounts.set(
                        categoryId,
                        currentCategoryAmount + balanceAmount,
                      );
                    }
                  }
                }
                return Promise.resolve(workerMessage);
              },
              (error: Error) => {
                return Promise.reject(error);
              },
            ),
          );
        }
      }
    }

    // set the results by intervals after all category promises are resolved
    await Promise.all(promises);

    budgetResult.resultsByIntervals = resultsByIntervals;
    return budgetResult;
  }

  /**
   * Setup the initial budget results structure
   * @param budgetResult
   * @param budgetView
   * @param budgetIntervalTimes
   * @param resultsByIntervals
   * @param startDate
   */
  static setupBudgetResults(
    budgetResult: BudgetResultView,
    budgetView: BudgetView,
    budgetIntervalTimes: number[],
    resultsByIntervals: Map<number, BudgetAmounts>,
    startDate: Date,
  ) {
    budgetResult.startDate = startDate;
    budgetResult.interval = budgetView.interval;
    budgetResult.timeStamps = budgetIntervalTimes;
    budgetResult.budgetedAmount = budgetView.amount;
    budgetResult.resultsByIntervals = resultsByIntervals;

    for (const intervalTime of budgetIntervalTimes) {
      const budgetAmounts: BudgetAmounts = {
        budgetAmount: budgetView.amount,
        actualAmount: 0,
        varianceAmount: budgetView.amount,
        categoryActualAmounts: new Map<string, number>(),
      };
      budgetResult.resultsByIntervals.set(intervalTime, budgetAmounts);
    }
  }

  /**
   * Get the budget interval times in unix timestamp format for the given start date, interval and number of periods
   * @param startDateStart
   * @param interval
   * @param periods
   */
  static getBudgetIntervalTimes(
    startDateStart: Date,
    interval: BudgetIntervalType,
    periods: number,
    getEndTime = false,
  ): number[] {
    const intervalTimes: number[] = [];
    const startDate = new Date(startDateStart);
    let currentDate: Date;

    for (let i = 1; i < periods + 2; i++) {
      currentDate = interval === "weekly" ? addWeeks(startDate, i) : addMonths(startDate, i);
      let endOfInterval = currentDate.getTime() / 1000;
      if (getEndTime) {
        endOfInterval = endOfInterval - 1;
      }
      intervalTimes.push(endOfInterval);
    }
    return intervalTimes;
  }
}
